import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { setAppTimezone } from "@/lib/datetime";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [tenant, setTenant] = useState(null);

  const fetchTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/tenants/me");
      setAppTimezone(data?.timezone);
      setTenant(data);
    } catch (e) {
      setTenant(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await fetchTenant();
    } catch (e) {
      setUser(false);
      setTenant(null);
    }
  }, [fetchTenant]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, totpCode) => {
    try {
      const payload = { email, password };
      if (totpCode) payload.totp_code = totpCode;
      const { data } = await api.post("/auth/login", payload);
      setUser(data.user);
      await fetchTenant();
      return { ok: true };
    } catch (e) {
      // F7 — Surface the 2FA-required signal to the UI so it can prompt
      // for the second factor without flashing a "wrong password" error.
      if (e?.response?.status === 428 && e?.response?.data?.detail?.two_factor_required) {
        return { ok: false, requires2fa: true };
      }
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      // ignore logout errors
    }
    setUser(false);
    setTenant(null);
  };

  return (
    <AuthContext.Provider value={{ user, tenant, login, logout, refresh, refreshUser: refresh, refreshTenant: fetchTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
